import {
  AI_RECOMMENDATION_GENERATION_MODE,
  AI_RECOMMENDATION_CONTEXT_SOURCE,
  collectBrowsingContext,
  extractShopperDisplayName,
  getAiRecommendationMeta,
  findFirstOfferProduct,
} from "../../shared/ai-recommendation-copy.js";
import { getApiBaseUrl } from "./api-base-url.js";

const AI_COPY_CACHE_TTL_MS = 10 * 60 * 1000;
const copyCache = new Map();
const inflightRequests = new Map();

/**
 * @param {object} [cart]
 */
function normalizeCartSnapshot(cart) {
  if (!cart) return null;
  if (cart.items) {
    return {
      total: cart.total ?? cart.total_price,
      itemCount: cart.itemCount ?? cart.item_count ?? cart.items?.length,
      items: cart.items,
    };
  }
  return cart;
}

function resolveShopifyCustomer() {
  if (typeof window === "undefined") return null;
  return window.Shopify?.customer || null;
}

function buildRuntimePageContext(runtimePage = {}) {
  if (typeof window === "undefined") {
    return runtimePage;
  }
  return {
    path: runtimePage.path ?? window.location.pathname,
    url: runtimePage.url ?? window.location.href,
    referrer: runtimePage.referrer ?? document.referrer ?? "",
    title: runtimePage.title ?? document.title ?? "",
  };
}

function buildRuntimeBrowsingContext(storageKeys) {
  if (typeof window === "undefined") return {};
  return collectBrowsingContext(storageKeys, {
    localStorage: window.localStorage,
    sessionStorage: window.sessionStorage,
    cookieString: document.cookie,
    shopifyCustomer: resolveShopifyCustomer(),
  });
}

function buildRequestCacheKey({ storeName, section, popupData, context }) {
  const aiMeta = getAiRecommendationMeta(section);
  const currentProduct = context?.currentProduct || {};
  const offerProduct = context?.offerProduct || {};
  const normalizeId = (product) => {
    const raw = product?.id ?? product?.product_id ?? "";
    const str = String(raw);
    return str.includes("/") ? str.split("/").pop() : str;
  };
  return JSON.stringify({
    storeName,
    popupId: popupData?._id || "",
    generationMode: aiMeta.generationMode,
    prompt: aiMeta.prompt || "",
    maxWords: aiMeta.maxWords,
    labelLine: aiMeta.labelLine || "",
    currentProductId: normalizeId(currentProduct),
    offerProductId: normalizeId(offerProduct),
    pagePath: context?.page?.path || "",
    cartCount:
      context?.cart?.itemCount ??
      (Array.isArray(context?.cart?.items) ? context.cart.items.length : 0),
    shopperName: context?.shopperName || "",
  });
}

function readCachedCopy(cacheKey) {
  const entry = copyCache.get(cacheKey);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    copyCache.delete(cacheKey);
    return null;
  }
  return entry.value;
}

function writeCachedCopy(cacheKey, value) {
  copyCache.set(cacheKey, {
    value,
    expiresAt: Date.now() + AI_COPY_CACHE_TTL_MS,
  });
}

/**
 * @param {object} params
 * @param {string} params.storeName
 * @param {object} params.section aiBanner section
 * @param {object} params.popupData
 * @param {object} [params.runtimeContext]
 */
export async function fetchRuntimeAiRecommendationCopy({
  storeName,
  section,
  popupData,
  runtimeContext = {},
}) {
  const aiMeta = getAiRecommendationMeta(section);
  if (aiMeta.generationMode === AI_RECOMMENDATION_GENERATION_MODE.STATIC) {
    return { text: String(section?.text || "").trim(), source: "static" };
  }

  const sources = new Set(aiMeta.contextSources);
  const offerProduct =
    runtimeContext.offerProduct || findFirstOfferProduct(popupData);

  if (
    sources.has(AI_RECOMMENDATION_CONTEXT_SOURCE.OFFER_PRODUCT) &&
    !offerProduct
  ) {
    return {
      text: String(section?.text || "").trim(),
      source: "awaiting-offer-product",
    };
  }

  const browsing = sources.has(AI_RECOMMENDATION_CONTEXT_SOURCE.BROWSING)
    ? buildRuntimeBrowsingContext(aiMeta.storageKeys)
    : undefined;

  const shopperName = extractShopperDisplayName(browsing, {
    shopifyCustomer: resolveShopifyCustomer(),
  });

  const context = {
    offerProduct: sources.has(AI_RECOMMENDATION_CONTEXT_SOURCE.OFFER_PRODUCT)
      ? offerProduct
      : undefined,
    cart: sources.has(AI_RECOMMENDATION_CONTEXT_SOURCE.CART)
      ? normalizeCartSnapshot(runtimeContext.cart)
      : undefined,
    currentProduct: sources.has(AI_RECOMMENDATION_CONTEXT_SOURCE.CURRENT_PRODUCT)
      ? runtimeContext.currentProduct
      : undefined,
    page: sources.has(AI_RECOMMENDATION_CONTEXT_SOURCE.PAGE)
      ? buildRuntimePageContext(runtimeContext.page)
      : undefined,
    browsing,
    shopperName: shopperName || undefined,
  };

  const cacheKey = buildRequestCacheKey({
    storeName,
    section,
    popupData,
    context,
  });

  const cached = readCachedCopy(cacheKey);
  if (cached) return cached;

  if (inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey);
  }

  const url = `${getApiBaseUrl()}/api/public/ai-recommendation-copy/${encodeURIComponent(storeName)}`;

  const requestPromise = (async () => {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        section,
        context,
        offerProduct,
        popupId: popupData?._id,
      }),
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody?.message || `AI copy request failed (${res.status})`);
    }

    const body = await res.json();
    const result = body?.data || body;
    writeCachedCopy(cacheKey, result);
    return result;
  })();

  inflightRequests.set(cacheKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    inflightRequests.delete(cacheKey);
  }
}
